<template>
    <div class="Breadcrumbs flex row-left-center">
        <div
            v-for="({
                label, icon, badge, to,
            }, index) in breadcrumbs"
            :key="`breadcrumb-${index}`"
            class="BreadcrumbsItem flex row-left-center"
        >
            <RouterLink
                :id="`breadcrumb-${index}`"
                :to="to"
                :class="['BreadcrumbsLink', 'flex', 'row-left-center', { 'is-active': index === length - 1 }]"
            >
                <q-icon
                    v-if="icon"
                    :name="icon"
                    size="18px"
                />
                <div class="q-mx-sm">
                    {{ label }}
                </div>
                <Badge
                    v-if="badge"
                    v-bind="badge"
                />
            </RouterLink>
            <q-icon
                v-if="(length > 1 && index + 1 < length)"
                :name="separator"
                class="BreadcrumbsSeparator"
            />
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { Api, apiRequest } from '@/api'
import { mediaQueryMixin } from '@/mixins'
import { Badge } from '@/components'

export default {
    name: 'BreadcrumbsComponent',
    components: {
        Badge,
    },
    mixins: [mediaQueryMixin],
    props: {
        separator: VueTypes.string.def('navigate_next'),
    },
    data() {
        return {
            organizationId: undefined,
            projectId: undefined,
            processId: undefined,
            queryId: undefined,
            organization: undefined,
            project: undefined,
            query: undefined,
            process: undefined,
            breadcrumbs: [],
        }
    },
    computed: {
        length() {
            return this.breadcrumbs.length
        },
        expanded() {
            return !this.isMobile
        },
    },
    watch: {
        '$route.fullPath': {
            handler() {
                this.syncFromRoute()
            },
            immediate: true,
        },
        expanded() {
            this.formatBreadcrumps()
        },
    },
    methods: {
        syncFromRoute() {
            const {
                organizationId, projectId, processId, queryId,
            } = this.$route.params || {}

            const organizationChanged = this.organizationId !== organizationId
            const projectChanged = this.projectId !== projectId
            const queryChanged = this.queryId !== queryId
            const processChanged = this.processId !== processId

            this.organizationId = organizationId
            this.projectId = projectId
            this.processId = processId
            this.queryId = queryId

            if (organizationChanged) this.organization = undefined
            if (projectChanged) this.project = undefined
            if (queryChanged) this.query = undefined
            if (processChanged) this.process = undefined

            this.formatBreadcrumps()

            if (this.organizationId && (organizationChanged || !this.organization)) this.getOrganization()
            if (this.projectId && (projectChanged || !this.project)) this.getProject()
            if (this.queryId && (queryChanged || !this.query)) this.getQuery()
            if (this.processId && (processChanged || !this.process)) this.getDataset()
        },
        formatBreadcrumps() {
            const {
                organization, organizationId, project, expanded, query, process, projectId,
            } = this
            const routeName = this.$route?.name

            const section = (() => {
                if (!organizationId) return undefined

                if (routeName === 'Invitations') return 'Invitations'
                if (routeName === 'Notifications') return 'Notifications'

                // Para cualquier ruta bajo proyecto (tiene projectId) y para la lista de projects.
                if (projectId || routeName === 'Projects') return 'Projects'

                return undefined
            })()

            const sectionBreadcrumbs = section
                ? [{
                    label: this.$t(`navigation.${section.toLowerCase()}`),
                    to: { name: section, params: { organizationId } },
                }]
                : []

            const isProfileRoute = routeName === 'Account' || routeName === 'Doc'

            const rootBreadcrumbs = isProfileRoute
                ? [{
                    label: this.$t('general.profile'),
                    to: { name: 'Account' },
                }]
                : [{
                    label: this.$t('navigation.organizations'),
                    to: { name: 'Organizations' },
                }]

            const apiTokenBreadcrumbs = routeName === 'Doc'
                ? [{
                    label: this.$t('general.apis'),
                    to: { name: 'Doc' },
                }]
                : []

            this.breadcrumbs = expanded
                ? [
                    ...rootBreadcrumbs,
                    ...apiTokenBreadcrumbs,
                    ...(organization
                        ? [{
                            label: organization.name,
                            to: { name: 'Organization', params: { organizationId } },
                        }]
                        : []),
                    ...sectionBreadcrumbs,
                    ...(project
                        ? [{
                            label: project.name,
                            to: { name: 'Project', params: { projectId: project.uuid } },
                            ...(project.deleteDate
                                ? {
                                    badge: {
                                        label: this.$t('general.readOnly'),
                                        icon: 'alert-triangle',
                                        color: 'warning',
                                        textColor: 'black',
                                    },
                                }
                                : {}),
                        }]
                        : []),
                    ...(query
                        ? [{
                            label: query.name,
                            to: { name: 'Query', params: { queryId: query.uuid } },
                        }]
                        : []),
                    ...(process && project
                        ? [{
                            label: process.name,
                            to: { name: 'ProcessVariants', params: { projectId, processId: process.uuid } },
                        }]
                        : []),
                ]
                : []
        },
        getOrganization() {
            apiRequest(Api().organizations.get({ organizationUid: this.organizationId }))
                .then(({ data }) => {
                    const [organization] = data
                    this.organization = organization
                    this.formatBreadcrumps()
                })
        },
        getProject() {
            apiRequest(Api().projects.get({ projectId: this.projectId }))
                .then(({ data }) => {
                    const [project] = data
                    this.project = project
                    this.formatBreadcrumps()
                })
        },
        getQuery() {
            apiRequest(Api().queries.get({ queryId: this.queryId }))
                .then(({ data }) => {
                    const [query] = data || []
                    this.query = query
                    this.formatBreadcrumps()
                })
        },
        getDataset() {
            apiRequest(Api().datasets.get({ datasetId: this.processId }))
                .then(({ data }) => {
                    const [process] = data || []
                    this.process = process
                    this.formatBreadcrumps()
                })
        },
    },
}
</script>

<style scoped lang="scss">
.BreadcrumbsLink {
    color: $gray-01;
    font-weight: 400;
    text-decoration: none;

    &.is-active {
        color: $primary;
        font-weight: 500;
    }

    .q-icon {
        color: inherit;
    }
}

.BreadcrumbsSeparator {
    color: $gray-01;
}
</style>
